import React, { useEffect, useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import ReportShell from "@/components/ReportShell";
import { Field, TextInput, TextArea, Select } from "@/components/ReportPrimitives";
import { buildDmrReport, dmrReviewChecklist, DMR_QUICK_PHRASES } from "@/lib/reports/dmr";
import { dmrStore } from "@/lib/reports/storage";
import { useEngineeringSession } from "@/lib/rce/useEngineeringSession";
import { resolve } from "@/lib/rce/resolver";
import {
  createFluidDocument, updateFluidDocument, rePullFluidContextSnapshot,
} from "@/lib/mud/dmr";
import { CONTEXT_VERSION } from "@/lib/mud/kinds";

const emptyState = () => ({
  id: null,
  header: {},
  mud: {},
  volumes: {},
  treatments: {},
  solidsControl: {},
  gainsLosses: {},
  notes: {},
  recommendations: {},
});

function QuickPhrases({ onPick, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {DMR_QUICK_PHRASES.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPick(p)}
          className="text-[10.5px] px-2 py-1 rounded-md"
          style={{ background: "rgba(22,24,29,0.6)", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)" }}
        >
          + {p}
        </button>
      ))}
    </div>
  );
}

export default function DailyMudReport() {
  const { session } = useEngineeringSession();
  const rceCtx = useMemo(() => ({
    projectId:    session?.projectId    || "",
    rigId:        session?.rigId        || "",
    wellId:       session?.wellId       || "",
    mudProgramId: session?.mudProgramId || "",
  }), [session]);

  const [fluidDocId, setFluidDocId] = useState(null);
  const [fluidSnapshot, setFluidSnapshot] = useState(null);

  // Pre-populate the mud section with resolver values when a session is
  // active. The user can still override anything they enter manually.
  const initialState = useMemo(() => {
    if (!session) return null;
    const inh = (path) => {
      const r = resolve(path, rceCtx);
      return (r.confidence !== "unknown" && r.confidence !== "conflict") ? r.value : "";
    };
    const header = {
      operator: inh("project.operator"),
      wellName: inh("well.name"),
      rigName:  inh("rig.name"),
    };
    const mud = {
      weight:     String(inh("mud.weightInPpg") || ""),
      pv:         String(inh("mud.plasticViscosityCp") || ""),
      yp:         String(inh("mud.yieldPointLbf100ft2") || ""),
      ph:         String(inh("mud.pH") || ""),
      sand:       String(inh("mud.sandContentPct") || ""),
      owr:        String(inh("mud.oilWaterRatio") || ""),
      fluidLoss:  String(inh("mud.fluidLossMl30min") || ""),
    };
    return { header, mud };
  }, [session?.projectId, session?.rigId, session?.wellId, session?.mudProgramId]);

  // RCE — context strip rendered above the action bar.
  const rceContextStrip = (session || fluidSnapshot) ? (
    <div
      data-testid="dmr-context-strip"
      className="card-rs p-3"
      style={{
        background: "rgba(255,179,0,0.06)",
        borderColor: "var(--rs-border)",
        borderLeft: "2px solid var(--rs-amber)",
      }}
    >
      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold mb-1" style={{ color: "var(--rs-amber)" }}>
        Engineering Context · {CONTEXT_VERSION}
        {fluidSnapshot?.frozen ? " · Frozen" : " · Pending freeze"}
      </div>
      <div className="text-[11px] text-[var(--rs-text-2)] grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5">
        {fluidSnapshot ? (
          <>
            <span><b>Operator:</b> {fluidSnapshot.project.operator || "—"}</span>
            <span><b>Well:</b>     {fluidSnapshot.well.name || "—"}</span>
            <span><b>Rig:</b>      {fluidSnapshot.rig.name || "—"}</span>
            <span><b>Mud Prog:</b> {fluidSnapshot.mud.programId || "—"}</span>
            <span><b>Weight:</b>   {fluidSnapshot.mud.weightInPpg || "—"}</span>
            <span><b>Captured:</b> {fluidSnapshot.capturedAt ? new Date(fluidSnapshot.capturedAt).toLocaleString() : "—"}</span>
          </>
        ) : (
          <span className="col-span-3 text-[var(--rs-text-3)]">
            Live preview — fluid context will be frozen on first save.
          </span>
        )}
      </div>
    </div>
  ) : null;

  // RCE — extra action button for explicit re-pull.
  const rceExtraAction = (
    <button
      data-testid="dmr-repull-btn"
      onClick={() => {
        if (!fluidDocId) { toast.error("Save the draft first before re-pulling."); return; }
        try {
          const refreshed = rePullFluidContextSnapshot(fluidDocId, rceCtx);
          setFluidSnapshot(refreshed.contextSnapshot);
          toast.success("Re-pulled engineering context from RCE");
        } catch (e) { toast.error(e.message || "Re-pull failed"); }
      }}
      disabled={!fluidDocId}
      title={fluidDocId ? "Refresh frozen engineering context" : "Save the draft first to enable re-pull"}
      className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5 disabled:opacity-40"
    >
      <RotateCcw className="w-3.5 h-3.5" /> Re-pull from Context
    </button>
  );

  const onAfterSave = (state /* , savedItem */) => {
    try {
      if (!fluidDocId) {
        const doc = createFluidDocument("dailyMudReport", {
          header:        state.header,
          properties:    {},   // tri-state lives on the Mud Program in RCE-3
          volumes:       state.volumes,
          treatments:    state.treatments,
          solidsControl: state.solidsControl,
          gainsLosses:   state.gainsLosses,
          notes:         state.notes,
          recommendations: state.recommendations,
        }, rceCtx);
        setFluidDocId(doc.id);
        setFluidSnapshot(doc.contextSnapshot);
      } else {
        const doc = updateFluidDocument(fluidDocId, { payload: {
          header: state.header, volumes: state.volumes,
          treatments: state.treatments, solidsControl: state.solidsControl,
          gainsLosses: state.gainsLosses, notes: state.notes,
          recommendations: state.recommendations,
        }});
        setFluidSnapshot(doc.contextSnapshot);
      }
    } catch (err) {
      console.warn("RCE DMR persist failed:", err);
    }
  };

  const sections = [
    {
      id: "header", badge: "01", title: "Report Header", defaultOpen: true,
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Report Date"><TextInput type="date" value={state.header.reportDate} onChange={(v) => set("header", "reportDate", v)} testId="dmr-h-reportDate" /></Field>
          <Field label="Rig Name"><TextInput value={state.header.rigName} onChange={(v) => set("header", "rigName", v)} testId="dmr-h-rigName" /></Field>
          <Field label="Well Name"><TextInput value={state.header.wellName} onChange={(v) => set("header", "wellName", v)} testId="dmr-h-wellName" /></Field>
          <Field label="Operator / Client"><TextInput value={state.header.operator} onChange={(v) => set("header", "operator", v)} testId="dmr-h-operator" /></Field>
          <Field label="Mud Engineer / Prepared By"><TextInput value={state.header.preparedBy} onChange={(v) => set("header", "preparedBy", v)} testId="dmr-h-preparedBy" /></Field>
          <Field label="Hole Section"><TextInput value={state.header.holeSection} onChange={(v) => set("header", "holeSection", v)} testId="dmr-h-holeSection" /></Field>
          <Field label="Current Depth"><TextInput type="number" value={state.header.currentDepth} onChange={(v) => set("header", "currentDepth", v)} testId="dmr-h-currentDepth" /></Field>
        </div>
      ),
    },
    {
      id: "mud", badge: "02", title: "Mud System & Properties",
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Mud Type"><Select value={state.mud.mudType} onChange={(v) => set("mud", "mudType", v)} options={["WBM", "OBM", "SBM", "Other"]} testId="dmr-m-mudType" /></Field>
          <Field label="Mud Weight (ppg)"><TextInput type="number" value={state.mud.mudWeight} onChange={(v) => set("mud", "mudWeight", v)} testId="dmr-m-mudWeight" /></Field>
          <Field label="Funnel Viscosity"><TextInput type="number" value={state.mud.funnelVisc} onChange={(v) => set("mud", "funnelVisc", v)} testId="dmr-m-funnelVisc" /></Field>
          <Field label="Plastic Viscosity (PV)"><TextInput type="number" value={state.mud.pv} onChange={(v) => set("mud", "pv", v)} testId="dmr-m-pv" /></Field>
          <Field label="Yield Point (YP)"><TextInput type="number" value={state.mud.yp} onChange={(v) => set("mud", "yp", v)} testId="dmr-m-yp" /></Field>
          <Field label="Gel Strengths (10s/10min/30min)"><TextInput value={state.mud.gels} onChange={(v) => set("mud", "gels", v)} testId="dmr-m-gels" /></Field>
          <Field label="pH"><TextInput type="number" value={state.mud.ph} onChange={(v) => set("mud", "ph", v)} testId="dmr-m-ph" /></Field>
          <Field label="Chlorides"><TextInput type="number" value={state.mud.chlorides} onChange={(v) => set("mud", "chlorides", v)} testId="dmr-m-chlorides" /></Field>
          <Field label="Sand Content (%)"><TextInput type="number" value={state.mud.sand} onChange={(v) => set("mud", "sand", v)} testId="dmr-m-sand" /></Field>
          <Field label="Solids (%)"><TextInput type="number" value={state.mud.solids} onChange={(v) => set("mud", "solids", v)} testId="dmr-m-solids" /></Field>
          <Field label="Oil/Water Ratio (OBM)"><TextInput value={state.mud.owr} onChange={(v) => set("mud", "owr", v)} testId="dmr-m-owr" /></Field>
          <Field label="Fluid Loss"><TextInput type="number" value={state.mud.fluidLoss} onChange={(v) => set("mud", "fluidLoss", v)} testId="dmr-m-fluidLoss" /></Field>
          <Field label="MBT"><TextInput type="number" value={state.mud.mbt} onChange={(v) => set("mud", "mbt", v)} testId="dmr-m-mbt" /></Field>
          <Field label="Mud Temperature"><TextInput type="number" value={state.mud.mudTemp} onChange={(v) => set("mud", "mudTemp", v)} testId="dmr-m-mudTemp" /></Field>
        </div>
      ),
    },
    {
      id: "volumes", badge: "03", title: "Pit / Volume Summary",
      render: (state, set) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Active Pit Volume"><TextInput type="number" value={state.volumes.activePit} onChange={(v) => set("volumes", "activePit", v)} testId="dmr-v-activePit" /></Field>
          <Field label="Reserve Pit Volume"><TextInput type="number" value={state.volumes.reservePit} onChange={(v) => set("volumes", "reservePit", v)} testId="dmr-v-reservePit" /></Field>
        </div>
      ),
    },
    {
      id: "treatments", badge: "04", title: "Treatments and Chemicals",
      render: (state, set) => (
        <>
          <Field label="Chemicals Added"><TextArea value={state.treatments.chemicalsAdded} onChange={(v) => set("treatments", "chemicalsAdded", v)} testId="dmr-t-chemicalsAdded" /></Field>
          <Field label="Treatments Performed"><TextArea value={state.treatments.treatments} onChange={(v) => set("treatments", "treatments", v)} testId="dmr-t-treatments" /></Field>
        </>
      ),
    },
    {
      id: "solidsControl", badge: "05", title: "Solids Control",
      render: (state, set) => (
        <Field label="Solids Control Equipment Status"><TextArea value={state.solidsControl.status} onChange={(v) => set("solidsControl", "status", v)} testId="dmr-sc-status" /></Field>
      ),
    },
    {
      id: "gainsLosses", badge: "06", title: "Gains / Losses",
      render: (state, set) => (
        <Field label="Gains / Losses Summary"><TextArea value={state.gainsLosses.summary} onChange={(v) => set("gainsLosses", "summary", v)} testId="dmr-gl-summary" /></Field>
      ),
    },
    {
      id: "notes", badge: "07", title: "Mud Engineer Notes",
      render: (state, set, helpers) => (
        <>
          <Field label="Rough Notes" helper="Write what happened in simple words.">
            <TextArea value={state.notes.roughNotes} onChange={(v) => set("notes", "roughNotes", v)} rows={4} testId="dmr-n-roughNotes" />
          </Field>
          <QuickPhrases onPick={(p) => {
            helpers.setState((s) => ({ ...s, notes: { ...s.notes, roughNotes: ((s.notes.roughNotes || "") + (s.notes.roughNotes && !/\s$/.test(s.notes.roughNotes || "") ? " " : "") + p) } }));
          }} testId="dmr-n-quick-phrases" />
        </>
      ),
    },
    {
      id: "recommendations", badge: "08", title: "Recommendations / Next Tour Plan",
      render: (state, set) => (
        <Field label="Next Tour Recommendations"><TextArea value={state.recommendations.nextTour} onChange={(v) => set("recommendations", "nextTour", v)} rows={3} testId="dmr-r-nextTour" /></Field>
      ),
    },
  ];

  return (
    <ReportShell
      testIdPrefix="dmr"
      reportType="dailyMudReport"
      safetyCritical={false}
      title="Daily Mud Report"
      initialState={initialState}
      rceContextStrip={rceContextStrip}
      rceExtraAction={rceExtraAction}
      onAfterSave={onAfterSave}
      subtitle="Mud properties, treatments, volumes, recommendations"
      brandLine="Daily Mud Report  ·  Draft Documentation Support"
      emptyState={emptyState}
      buildFn={buildDmrReport}
      checklistFn={dmrReviewChecklist}
      store={dmrStore}
      sections={sections}
      filenameFromState={(s) => {
        const safe = (v) => (v || "").toString().replace(/[^a-z0-9-_]+/gi, "_").slice(0, 30);
        return `RigSentinel_DMR_${safe(s.header.wellName) || "well"}_${(s.header.reportDate || "").replace(/[^0-9]/g, "") || Date.now()}.pdf`;
      }}
    />
  );
}
